import { apiConfig } from '@nomiss/configs';
import { parseApiErrorResponse } from './api-request.util';
import {
  buildApiHeaders,
  fetchWithAuthRecovery,
  resolveAccessToken,
} from './sdk-client';

export type UserSettings = {
  language_id: string;
  language_code: string;
  digest_time_id?: string | null;
  timezone_override_id?: string | null;
  spoiler_global_enabled?: boolean;
  notification_enabled?: boolean;
  updated_at: string;
};

export type PatchUserSettingsInput = {
  language_code?: string;
  language_id?: string;
};

async function userSettingsRequest<T>(
  path: string,
  init?: RequestInit,
  accessToken?: string | null,
): Promise<T> {
  const token = accessToken ?? resolveAccessToken();
  const headers = buildApiHeaders({
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  });

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetchWithAuthRecovery(`${apiConfig.baseUrl}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    throw await parseApiErrorResponse(response, 'User settings API failed');
  }

  const json = (await response.json()) as { data: T } | T;
  return typeof json === 'object' && json !== null && 'data' in json
    ? (json as { data: T }).data
    : (json as T);
}

export async function getMySettings(
  accessToken?: string | null,
): Promise<UserSettings> {
  return userSettingsRequest<UserSettings>(
    '/v1/users/me/settings',
    undefined,
    accessToken,
  );
}

export async function patchMySettings(
  input: PatchUserSettingsInput,
  accessToken?: string | null,
): Promise<UserSettings> {
  return userSettingsRequest<UserSettings>(
    '/v1/users/me/settings',
    {
      method: 'PATCH',
      body: JSON.stringify(input),
    },
    accessToken,
  );
}
